import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, Boxes, Pencil, Plus, Trash2 } from 'lucide-react';

import type { Component } from '@tasks/contracts';

import {
  archiveComponent,
  createComponent,
  deleteComponent,
  listComponents,
  restoreComponent,
  updateComponent,
} from '../../api/components';
import { queryKeys } from '../../api/queryKeys';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState } from '../../components/EmptyState';
import { IconButton } from '../../components/IconButton';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Select } from '../../components/Select';
import { Skeleton } from '../../components/Skeleton';
import { Textarea } from '../../components/Textarea';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';
import { useProjectMembers } from '../../hooks/useProject';
import styles from './SettingsPages.module.css';

const NO_USER = '__none__';

/**
 * Компоненты проекта (docs/13 §7, UC-PRJ-06).
 *
 * Компонент — подсистема проекта: маршрутизирует задачи на владельца и
 * исполнителя по умолчанию, но задачами не владеет. Удаление не удаляет
 * задачи, поэтому основной способ убрать компонент — архивация.
 */
export function ComponentsSettingsPage() {
  const { key = '' } = useParams<{ key: string }>();
  const queryClient = useQueryClient();
  const toast = useToast();
  const members = useProjectMembers(key);

  const [includeArchived, setIncludeArchived] = useState(false);
  const [isFormOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Component | null>(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    leadId: NO_USER,
    defaultAssigneeId: NO_USER,
  });
  const [componentToDelete, setComponentToDelete] = useState<Component | null>(null);

  const query = useQuery({
    queryKey: queryKeys.projects.components(key, includeArchived),
    queryFn: ({ signal }) => listComponents(key, { includeArchived, signal }),
    enabled: Boolean(key),
  });
  const components = query.data ?? [];

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['projects', key, 'components'] });
  };

  const reportError = (cause: unknown): void => {
    const human = humanizeError(cause);
    toast.error(human.message, { code: human.code });
  };

  const memberOptions = [
    { value: NO_USER, label: 'Не задан' },
    ...members.map((member) => ({ value: member.user.id, label: member.user.displayName })),
  ];

  const saveMutation = useMutation({
    mutationFn: () =>
      editing
        ? updateComponent(key, editing.id, {
            name: form.name.trim(),
            description: form.description,
            leadId: form.leadId === NO_USER ? null : form.leadId,
            defaultAssigneeId: form.defaultAssigneeId === NO_USER ? null : form.defaultAssigneeId,
          })
        : createComponent(key, {
            name: form.name.trim(),
            description: form.description,
            leadId: form.leadId === NO_USER ? null : form.leadId,
            defaultAssigneeId: form.defaultAssigneeId === NO_USER ? null : form.defaultAssigneeId,
          }),
    onSuccess: (component) => {
      setFormOpen(false);
      setEditing(null);
      invalidate();
      toast.success(editing ? 'Компонент обновлён' : `Компонент «${component.name}» создан`);
    },
    onError: reportError,
  });

  const archiveMutation = useMutation({
    mutationFn: (component: Component) =>
      component.archivedAt === null
        ? archiveComponent(key, component.id)
        : restoreComponent(key, component.id),
    onSuccess: (component) => {
      invalidate();
      toast.success(
        component.archivedAt === null ? 'Компонент восстановлен' : 'Компонент в архиве',
      );
    },
    onError: reportError,
  });

  const deleteMutation = useMutation({
    mutationFn: (component: Component) => deleteComponent(key, component.id),
    onSuccess: () => {
      setComponentToDelete(null);
      invalidate();
      toast.success('Компонент удалён');
    },
    onError: reportError,
  });

  const openCreate = (): void => {
    setEditing(null);
    setForm({ name: '', description: '', leadId: NO_USER, defaultAssigneeId: NO_USER });
    setFormOpen(true);
  };

  const openEdit = (component: Component): void => {
    setEditing(component);
    setForm({
      name: component.name,
      description: component.description,
      leadId: component.lead?.id ?? NO_USER,
      defaultAssigneeId: component.defaultAssignee?.id ?? NO_USER,
    });
    setFormOpen(true);
  };

  return (
    <div className={styles['section']}>
      <div className={styles['sectionHeader']}>
        <h2>Компоненты</h2>
        <Button variant="primary" icon={<Plus size={15} />} onClick={openCreate}>
          Создать компонент
        </Button>
      </div>

      <p className={styles['hint']}>
        Компонент — подсистема проекта («Платежи», «Отчёты»): он маршрутизирует
        задачи на владельца и исполнителя по умолчанию. Задачи компоненту не
        принадлежат — удаление компонента их не удаляет. Уникальность имени — без
        учёта регистра, до 50 активных компонентов.
      </p>

      <label className={styles['checkboxRow']}>
        <input
          type="checkbox"
          checked={includeArchived}
          onChange={(event) => setIncludeArchived(event.target.checked)}
        />
        Показывать архивные
      </label>

      {query.isPending ? (
        <Skeleton variant="row" count={3} />
      ) : components.length === 0 ? (
        <EmptyState
          icon={<Boxes size={20} />}
          title="Компонентов ещё нет"
          description="Создайте первый компонент, чтобы маршрутизировать задачи по подсистемам проекта."
        />
      ) : (
        <ul className={styles['boardList']}>
          {components.map((component) => (
            <li
              key={component.id}
              className={component.archivedAt === null ? styles['boardRow'] : styles['boardRowMuted']}
            >
              <span aria-hidden className={styles['boardIcon']}>
                <Boxes size={16} />
              </span>
              <span className={styles['boardName']}>{component.name}</span>
              {component.archivedAt === null ? null : (
                <span className={styles['boardDefault']}>в архиве</span>
              )}
              <span className={styles['componentMeta']}>
                {component.lead ? `владелец: ${component.lead.displayName}` : 'без владельца'}
                {' · '}
                {component.defaultAssignee
                  ? `по умолчанию: ${component.defaultAssignee.displayName}`
                  : 'без исполнителя по умолчанию'}
                {' · '}
                {`задач: ${component.issueCount}`}
              </span>

              <span className={styles['spacer']} />

              <IconButton
                size="sm"
                label={
                  component.archivedAt === null
                    ? `Архивировать компонент «${component.name}»`
                    : `Восстановить компонент «${component.name}»`
                }
                icon={component.archivedAt === null ? <Archive size={15} /> : <ArchiveRestore size={15} />}
                onClick={() => archiveMutation.mutate(component)}
              />
              <IconButton
                size="sm"
                label={`Переименовать компонент «${component.name}»`}
                icon={<Pencil size={15} />}
                onClick={() => openEdit(component)}
              />
              <IconButton
                size="sm"
                label={`Удалить компонент «${component.name}»`}
                icon={<Trash2 size={15} />}
                onClick={() => setComponentToDelete(component)}
              />
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={isFormOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? 'Изменить компонент' : 'Новый компонент'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setFormOpen(false)} disabled={saveMutation.isPending}>
              Отмена
            </Button>
            <Button
              variant="primary"
              loading={saveMutation.isPending}
              disabled={form.name.trim().length === 0}
              onClick={() => saveMutation.mutate()}
            >
              Сохранить
            </Button>
          </>
        }
      >
        <Input
          label="Название компонента"
          maxLength={60}
          value={form.name}
          placeholder="Например, Платежи"
          hint="Уникально внутри проекта; регистр сохраняется"
          onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
        />
        <Textarea
          label="Описание"
          rows={3}
          maxLength={2000}
          value={form.description}
          onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
        />
        <Select
          label="Владелец"
          options={memberOptions}
          value={form.leadId}
          onChange={(value) => setForm((prev) => ({ ...prev, leadId: value }))}
          placeholder="Не задан"
        />
        <Select
          label="Исполнитель по умолчанию"
          options={memberOptions}
          value={form.defaultAssigneeId}
          onChange={(value) => setForm((prev) => ({ ...prev, defaultAssigneeId: value }))}
          placeholder="Не задан"
        />
      </Modal>

      <ConfirmDialog
        open={componentToDelete !== null}
        title="Удалить компонент?"
        description={
          componentToDelete
            ? `Компонент «${componentToDelete.name}» будет удалён безвозвратно. Удалить можно только компонент без задач — иначе архивируйте его.`
            : ''
        }
        confirmLabel="Удалить"
        isPending={deleteMutation.isPending}
        onClose={() => setComponentToDelete(null)}
        onConfirm={() => {
          if (componentToDelete) deleteMutation.mutate(componentToDelete);
        }}
      />
    </div>
  );
}
